import { ReportRequest } from '@/client/reports';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import {
  useCourseCompletionsReports,
  usePhishingClickRateReports,
  useSCSurveyScoreReports,
} from '@/hooks/query';
import { CardReports, EmptyCardReports } from '@/ui/styled-ui';
import { ProductType } from '@/utils/helpers';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledCardReportsContainer = styled(CardReports)`
  &#o1-v2 {
    grid-area: o1;
  }
  &#o2-v2 {
    grid-area: o2;
  }
  &#o3-v2 {
    grid-area: o3;
  }
  display: flex;
  flex-direction: column;
  padding: var(--default-padding);
  border: 1px solid var(--gray-border-light);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;
`;

const StyledEmptyCardReportsContainer = styled(EmptyCardReports)`
  &#o1-empty {
    grid-area: o1;
  }
  &#o2-empty {
    grid-area: o2;
  }
  &#o3-empty {
    grid-area: o3;
  }
`;

type OverviewReportsPropsType = {
  accountId: string;
  filters: ReportRequest;
  t: TranslationFunctionType;
  activeAwarenessTraining: boolean;
  activePhishing: boolean;
  activeSecurityCulture: boolean;
};

export const OverviewReportsV2 = ({
  accountId,
  filters,
  t,
  activeAwarenessTraining,
  activePhishing,
  activeSecurityCulture,
}: OverviewReportsPropsType) => {
  const navigate = useNavigate();

  const { courseCompletions, isLoading: areCourseCompletionsLoading } =
    useCourseCompletionsReports(accountId, filters);

  const { phishingClickRate, isLoading: arePhishingClickRateLoading } =
    usePhishingClickRateReports(accountId, filters);

  const { scSurveyScore, isLoading: areSCSurveyScoreLoading } =
    useSCSurveyScoreReports(accountId, filters);

  const getRatePercentage = (
    courseCompletions: any[] | undefined,
    isSC?: boolean,
    isYearly?: boolean,
  ) => {
    if (!courseCompletions || courseCompletions.length === 0) {
      return { rate: 0, monthlyChange: null };
    }

    if (isYearly) {
      const currentYear = moment().format('YYYY');
      const currentYearData = courseCompletions.find((item) => {
        return (item?.period || '').split('-')[0] === currentYear;
      });

      if (!currentYearData) {
        return { rate: 0, monthlyChange: null };
      }

      if (isSC) {
        const currentYearScore = Number(currentYearData.score || 0);
        return {
          rate: Number(currentYearScore.toFixed(1)),
          monthlyChange: null,
        };
      }

      const currentYearTotal = Number(currentYearData.total || 0);
      const currentYearCompleted = Number(
        currentYearData.completed || currentYearData.clickedLink || 0,
      );

      if (currentYearTotal === 0) {
        return { rate: 0, monthlyChange: null };
      }

      const currentYearRate = Number(
        ((currentYearCompleted / currentYearTotal) * 100).toFixed(1),
      );

      return { rate: currentYearRate, monthlyChange: null };
    }

    const currentMonth = moment().format('YYYY-MM');
    const currentData = courseCompletions.find((item) => {
      const itemDate = moment(item.period);
      return itemDate.format('YYYY-MM') === currentMonth;
    });

    const lastMonthTarget = moment().subtract(1, 'month');
    const lastMonthData = courseCompletions.find((item) => {
      const itemDate = moment(item.period);
      return itemDate.format('YYYY-MM') === lastMonthTarget.format('YYYY-MM');
    });

    if (!currentData && !lastMonthData) {
      return { rate: 0, monthlyChange: null };
    }

    const currentTotal = Number(currentData?.total || 0);
    const currentCompleted = Number(
      currentData?.completed || currentData?.clickedLink || 0,
    );

    const currentRate =
      currentTotal > 0
        ? Number(((currentCompleted / currentTotal) * 100).toFixed(1))
        : 0;

    let monthlyChange = null;
    let isLow = false;
    if (lastMonthData) {
      const lastTotal = Number(lastMonthData.total || 0);
      const lastCompleted = Number(
        lastMonthData.completed || lastMonthData.clickedLink || 0,
      );

      if (lastTotal > 0) {
        const lastRate = Number(((lastCompleted / lastTotal) * 100).toFixed(1));
        const difference = Number((currentRate - lastRate).toFixed(1));
        const sign = difference >= 0 ? '+' : '';
        monthlyChange = `${sign}${difference}% from last month`;
        isLow = difference < 0;
      }
    }

    if (isSC) {
      let monthlyChange = null;
      let isLow = false;
      let currentScore = 0;
      if (lastMonthData) {
        const lastScore = Number(lastMonthData.score || 0);
        currentScore = Number(currentData?.score || 0);
        const difference = Number((currentScore - lastScore).toFixed(1));
        const sign = Number(difference) >= 0 ? '+' : '';
        monthlyChange = `${sign}${difference} from last month`;
        isLow = Number(difference) < 0;
      }

      return {
        rate: Number(currentScore.toFixed(1)),
        monthlyChange: monthlyChange,
        isLow,
      };
    }

    return {
      rate: currentRate,
      monthlyChange: monthlyChange,
      isLow,
    };
  };

  const completionData = getRatePercentage(
    courseCompletions,
    false,
    !filters?.from && !filters?.to,
  );
  const phishingClickRateData = getRatePercentage(
    phishingClickRate,
    false,
    !filters?.from && !filters?.to,
  );
  const scSurveyData = getRatePercentage(
    scSurveyScore,
    true,
    !filters?.from && !filters?.to,
  );

  return (
    <>
      {activeAwarenessTraining ? (
        <StyledCardReportsContainer
          id="o1-v2"
          isLoading={areCourseCompletionsLoading}
          title={t('dashboard.overview.awareness.title')}
          type={ProductType.AWARENESS}
          count={completionData.rate}
          isPercentage
          {...(completionData.monthlyChange && {
            footer: completionData.monthlyChange,
            isLow: completionData.isLow,
          })}
          onClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.REPORTS_AWARENESS]())
          }
        />
      ) : (
        <StyledEmptyCardReportsContainer
          id="o1-empty"
          type={ProductType.AWARENESS}
        />
      )}
      {activePhishing ? (
        <StyledCardReportsContainer
          id="o2-v2"
          isLoading={arePhishingClickRateLoading}
          title={t('dashboard.overview.phishing.title')}
          type={ProductType.PHISHING}
          count={phishingClickRateData.rate}
          isPercentage
          {...(phishingClickRateData.monthlyChange && {
            footer: phishingClickRateData.monthlyChange,
            isLow: phishingClickRateData.isLow,
          })}
          onClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.PHISHING_ANALYTICS]())
          }
        />
      ) : (
        <StyledEmptyCardReportsContainer
          id="o2-empty"
          type={ProductType.PHISHING}
        />
      )}
      {activeSecurityCulture ? (
        <StyledCardReportsContainer
          id="o3-v2"
          isLoading={areSCSurveyScoreLoading}
          title={t('dashboard.overview.sc.title')}
          type={ProductType.SECURITY_CULTURE}
          count={scSurveyData.rate}
          {...(scSurveyData.monthlyChange && {
            footer: scSurveyData.monthlyChange,
            isLow: scSurveyData.isLow,
          })}
          onClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY]())
          }
        />
      ) : (
        <StyledEmptyCardReportsContainer
          id="o3-empty"
          type={ProductType.SECURITY_CULTURE}
        />
      )}
    </>
  );
};
